import type { QuestionSetSummary } from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import { Skeleton } from '@repo/ui/components/skeleton';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { SetCard } from '../../components/sets/SetCard';
import { QueryErrorState } from '../../components/shell/QueryErrorState';
import { useListQuestionSets } from '../../queries/sets';
import { useAllTaxonomyTrees } from '../../queries/taxonomy';

export const Route = createFileRoute('/_admin/sets')({
  component: SetsRoute,
});

function SetsRoute() {
  const navigate = useNavigate();
  const { data: sets, isLoading, isError, error, refetch } = useListQuestionSets();
  const { data: trees } = useAllTaxonomyTrees();
  const [query, setQuery] = useState('');

  const topicLabels = useMemo(() => buildTopicLabelMap(trees), [trees]);
  const filtered = useMemo(
    () => filterSets(sets ?? [], query, topicLabels),
    [sets, query, topicLabels],
  );

  return (
    <div className="h-full w-full flex flex-col overflow-hidden px-6 pt-8">
      <header className="flex items-center justify-between gap-4 mb-5 shrink-0">
        <div>
          <h1 className="font-sans text-[20px] font-semibold text-ink">প্রশ্নসেট লাইব্রেরি</h1>
          <p className="text-[12px] text-ink-dim mt-1">সংরক্ষিত প্রশ্নসেট খুঁজুন, খুলুন, বা সম্পাদনা করুন।</p>
        </div>
        <Button
          variant="default"
          onClick={() => navigate({ to: '/add', search: { setId: undefined } })}
        >
          নতুন সেট
        </Button>
      </header>

      <div className="mb-4 shrink-0">
        <Input
          type="search"
          placeholder="নাম বা টপিক দিয়ে খুঁজুন…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="max-w-md rounded-sm"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading && <SetsListSkeleton />}
        {isError && (
          <QueryErrorState
            title="সেট লোড করা যায়নি"
            message={(error as Error | undefined)?.message ?? 'অজানা ত্রুটি'}
            onRetry={() => void refetch()}
          />
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <EmptyState
            query={query}
            onCreate={() => navigate({ to: '/add', search: { setId: undefined } })}
            totalCount={sets?.length ?? 0}
          />
        )}
        {!isLoading && !isError && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-8">
            {filtered.map((set) => (
              <SetCard key={set.id} set={set} topicLabel={topicLabels.get(set.topicId) ?? ''} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SetsListSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-[88px] w-full rounded-sm" />
      ))}
    </div>
  );
}

function EmptyState({
  query,
  totalCount,
  onCreate,
}: {
  query: string;
  totalCount: number;
  onCreate: () => void;
}) {
  const isFiltered = query.trim().length > 0 && totalCount > 0;
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] text-center py-12">
      <p className="font-sans text-[15px] text-ink mb-2">
        {isFiltered ? 'মিল পাওয়া যায়নি' : 'এখনো কোনো সেট নেই'}
      </p>
      <p className="text-[12px] text-ink-dim mb-4 max-w-sm">
        {isFiltered
          ? 'অন্য শব্দে খোঁজার চেষ্টা করুন, অথবা একটি নতুন সেট তৈরি করুন।'
          : 'প্রথম প্রশ্নসেটটি তৈরি করুন।'}
      </p>
      <Button onClick={onCreate}>নতুন সেট</Button>
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────────────────── */

type TaxonomyTreesData = ReturnType<typeof useAllTaxonomyTrees>['data'];

/**
 * Flatten the cached taxonomy tree to a `topicId → "<subject> · <chapter> ·
 * <topic>"` map. Used for client-side search to match either set name or any
 * level of the resolved breadcrumb.
 */
function buildTopicLabelMap(trees: TaxonomyTreesData): Map<number, string> {
  const map = new Map<number, string>();
  if (!trees) return map;
  for (const curriculum of trees) {
    for (const version of curriculum.versions) {
      for (const cls of version.classes) {
        for (const subject of cls.subjects) {
          for (const chapter of subject.chapters) {
            for (const topic of chapter.topics) {
              map.set(topic.id, `${subject.name} · ${chapter.name} · ${topic.name}`);
            }
          }
        }
      }
    }
  }
  return map;
}

function filterSets(
  sets: QuestionSetSummary[],
  query: string,
  topicLabels: Map<number, string>,
): QuestionSetSummary[] {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) return sets;
  return sets.filter((set) => {
    const name = (set.name ?? '').toLowerCase();
    const topic = topicLabels.get(set.topicId)?.toLowerCase() ?? '';
    return name.includes(trimmed) || topic.includes(trimmed);
  });
}
